import React, { useMemo, FC, ChangeEvent, ChangeEventHandler } from 'react';
import RSelect from 'react-select';
import { Form, FormikProvider } from 'formik';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import cn from 'classnames';
import Button from 'components/UI/Buttons/buttons';
import Input from 'components/UI/Input/input';
import Loader from 'components/UI/Loader';
import CardsList from 'components/UI/CardList';
import ToggleSwitch from 'components/UI/ToggleSwitch';
import Card from 'components/UI/Card';
import Checkbox from 'components/UI/Checkbox/checkbox';
import { isDisableEridToggle } from 'utils/isDisableEridToggle';
import Help from 'components/Help';
import PriceInput from 'components/UI/PriceInput';
import CollapseCard from 'components/UI/CollapseCard';
import { useExperimentalData } from 'utils/useExperimentalData';
import { isDisableSelfService } from 'utils/MethodRecalcHelpers/utils';
import SelectV2 from '../../../components/UI/SelectV2';
import css from './styles.module.scss';
import { useSRMethod, useCampaignSettigns } from './hooks';
import ErrorReport from '../../../components/Errors/Reports';
import HeaderCampaign from '../HeaderCampaign';
import { TReportItem } from '../types';
import { useCampaignInfo } from '../model/selectors';
import { useCampaignHint } from '../model/hooks/useCampaignHint';
import VariableNumberField from './VariableNumberField';
import Widget from './Widget';
import ExtendedReport from './ExtendedReport';
import Reports from './Reports';
import LinkingExternal from './LinkingExternal';
import './style.scss';
import AdvancedSettings from './AdvancedSettings';

const Settings: FC = () => {
  const { t } = useTranslation();

  const { data: campaign, isLoading, error } = useCampaignInfo();

  const isDisableSelf = isDisableSelfService(campaign);

  const {
    SRMethod,
    listSRMethods,
    onChangeSRMethod,
    optTag,
    onChangeText,
    aggregationTag,
    onChangeAggregation,
  } = useSRMethod(campaign);

  const {
    cpcData,
    marginalityData,
    configValuesIds,
    commissionAgencyData,
    commissionSelfServiceData,
  } = useExperimentalData();

  const { formik, isLoadingEdit, audiences, setCurrentAudit } =
    useCampaignSettigns({
      SRMethod,
      aggregationTag,
      configValuesIds,
      marginalityId: marginalityData?.id,
      cpcId: cpcData?.id,
      commissionAgencyData,
      commissionSelfServiceData,
    });

  const { showHint, closeHint } = useCampaignHint(
    formik.values.hidden_statistic,
  );

  const componentsSelect: {
    IndicatorSeparator: FC;
    DropdownIndicator: FC;
    Menu?: FC;
  } = useMemo(() => {
    if (optTag.length > 0) {
      return {
        IndicatorSeparator: () => null,
        DropdownIndicator: () => null,
      };
    }
    return {
      IndicatorSeparator: () => null,
      DropdownIndicator: () => null,
      Menu: () => null,
    };
  }, [optTag.length]);

  if (error.isError) {
    return <ErrorReport error={error} />;
  }

  if (isLoading && !error.isError) {
    return <Loader />;
  }

  const convertReports = ({ is_enabled }: TReportItem) => {
    if (is_enabled) {
      return t('clients_page.client_edit.included');
    }

    return t('clients_page.client_edit.turned_off');
  };

  const onKeyHandler = (keyboardEvent: React.KeyboardEvent) => {
    const { keyCode } = keyboardEvent;
    if (keyCode === 13) {
      keyboardEvent.preventDefault();
    }
  };

  // eslint-disable-next-line consistent-return
  const handlerAdditionalMetrics: ChangeEventHandler<HTMLInputElement> = (
    e,
    // eslint-disable-next-line consistent-return
  ) => {
    const { value } = e.target;
    const pattern = /^[0-9\s.\n]*$/;
    if (pattern.test(value)) {
      return formik.setFieldValue('additional_metrics', value);
    }
  };

  return (
    <>
      <Helmet>
        <title>
          {t('campaigns_page.campaign_settings.additional.set_tit')}
        </title>
      </Helmet>
      <HeaderCampaign campaign={campaign} isLoading={isLoading} />
      <div className={cn(css.container, 'container')}>
        <FormikProvider value={formik}>
          <CardsList>
            <Form>
              <Card
                noHover
                className={css.card}
                showHint={showHint}
                closeHint={closeHint}
                positionHint={250}
                textHint={
                  <>
                    <strong>
                      {t(
                        'campaigns_page.campaign_settings.additional.hide_real_statistics.hintStrong',
                      )}
                    </strong>{' '}
                    <span>
                      {t(
                        'campaigns_page.campaign_settings.additional.hide_real_statistics.hintSpan',
                      )}
                    </span>
                  </>
                }
              >
                <CollapseCard
                  closedStart
                  noUpdateHeight
                  title={
                    <h3 className={css.title}>
                      {t('campaigns_page.campaign_settings.additional.method')}
                    </h3>
                  }
                >
                  <div className={css.form}>
                    {campaign && (
                      <div>
                        <div className={css.label}>
                          {t(
                            'campaigns_page.campaign_settings.additional.type',
                          )}
                        </div>
                        <div className={cn(css.info, 'isUppercase')}>
                          {campaign.pay_type}
                        </div>
                      </div>
                    )}
                    <SelectV2
                      disabled={isDisableSelf}
                      value={SRMethod}
                      label={t(
                        'campaigns_page.campaign_settings.ListSRMethods.label',
                      )}
                      placeholder={t(
                        'campaigns_page.campaign_settings.additional.ddmm',
                      )}
                      options={listSRMethods || []}
                      customChange={onChangeSRMethod}
                    />
                    {cpcData && SRMethod === 'MAX_FIX_CPC_MARGINALITY' && (
                      <VariableNumberField
                        decimalScale={0}
                        fieldData={cpcData}
                        formik={formik}
                        onKeyHandler={onKeyHandler}
                      />
                    )}
                    {SRMethod === 'SELF_SERVICE_MARKUP' && (
                      <div className={css.item}>
                        <Checkbox
                          label={t(`campaigns_page.recalc_bid`)}
                          inputAttributes={{
                            type: 'checkbox',
                            name: 'is_recalculating',
                            checked: formik.values.is_recalculating,
                            onChange: () =>
                              formik.setFieldValue(
                                'is_recalculating',
                                !formik.values.is_recalculating,
                              ),
                          }}
                        />
                        <Help
                          title=""
                          helpattributes={{
                            text: t(`campaigns_page.recalc_bid_prompt`),
                          }}
                        />
                      </div>
                    )}
                    {SRMethod === 'SELF_SERVICE_MARKUP' && (
                      <VariableNumberField
                        decimalScale={3}
                        fieldData={commissionAgencyData}
                        formik={formik}
                        onKeyHandler={onKeyHandler}
                        disabled={isDisableSelf}
                      />
                    )}
                    {SRMethod === 'SELF_SERVICE_MARKUP' && (
                      <VariableNumberField
                        decimalScale={3}
                        fieldData={commissionSelfServiceData}
                        formik={formik}
                        onKeyHandler={onKeyHandler}
                        disabled={isDisableSelf}
                      />
                    )}
                    {marginalityData &&
                      SRMethod === 'MAX_FIX_CPC_MARGINALITY' && (
                        <VariableNumberField
                          decimalScale={3}
                          fieldData={marginalityData}
                          formik={formik}
                          onKeyHandler={onKeyHandler}
                        />
                      )}
                    {SRMethod === 'MAX_FIX_CPC_MARGINALITY' && (
                      <div className={css.field_item}>
                        <PriceInput
                          classname={css.number_format}
                          decimalScale={3}
                          theme="horizontal"
                          label={t(
                            'campaigns_page.campaign_settings.ListSRMethods.hidden_coef.label',
                          )}
                          hasZero
                          value={formik.values.hidden_commissions}
                          error={!!formik.errors.hidden_commissions}
                          errortext={formik.errors.hidden_commissions}
                          onValueChange={({ floatValue }) => {
                            formik.setFieldValue(
                              'hidden_commissions',
                              floatValue,
                            );
                          }}
                        />
                        <Help
                          title=""
                          helpattributes={{
                            text: `Процент скрытой комиссии. Влияет на расчет маржи \nMargin= (spent - minus_win_raw- spent*СК(в процентах))`,
                          }}
                        />
                      </div>
                    )}
                    <Checkbox
                      label={t(
                        'campaigns_page.campaign_settings.additional.hide_real_statistics.title',
                      )}
                      inputAttributes={{
                        type: 'checkbox',
                        name: 'hidden_statistic',
                        checked: formik.values.hidden_statistic,
                        disabled: isDisableSelf,
                        onChange: () =>
                          formik.setFieldValue(
                            'hidden_statistic',
                            !formik.values.hidden_statistic,
                          ),
                      }}
                    />
                    {SRMethod === 'COEFFICIENTS' && (
                      <Input
                        label={t(
                          'campaigns_page.campaign_settings.additional.coefficient',
                        )}
                        isTextarea
                        error={!!formik.errors.statistic_coefficient}
                        errortext={formik.errors.statistic_coefficient}
                        inputAttributes={{
                          type: 'textarea',
                          name: 'statistic_coefficient',
                          // eslint-disable-next-line consistent-return
                          onChange: (e: ChangeEvent<HTMLTextAreaElement>) => {
                            const { value } = e.target;
                            const pattern = /^[0-9\s.\n]*$/;
                            if (pattern.test(value)) {
                              return formik.setFieldValue(
                                'statistic_coefficient',
                                value,
                              );
                            }
                          },
                          placeholder: t(
                            'campaigns_page.campaign_settings.ListSRMethods.enter_data',
                          ),
                          value: formik.values.statistic_coefficient,
                          style: { minHeight: 256 },
                        }}
                        tooltip={{
                          text: (
                            <Trans
                              i18nKey="campaigns_page.campaign_settings.click_hint.general"
                              components={{ br: <br />, b: <strong /> }}
                            />
                          ),
                        }}
                      />
                    )}
                    {SRMethod !== 'MAX_FIX_CPC_MARGINALITY' && (
                      <Input
                        label={t(
                          'campaigns_page.campaign_settings.additional.clicks',
                        )}
                        isTextarea
                        error={!!formik.errors.additional_metrics}
                        errortext={formik.errors.additional_metrics}
                        inputAttributes={{
                          name: 'additional_metrics',
                          placeholder: t(
                            'campaigns_page.campaign_settings.enter_data_tip',
                          ),
                          onChange: handlerAdditionalMetrics,
                          style: { minHeight: 246 },
                          value: formik.values.additional_metrics,
                        }}
                        tooltip={{
                          text: (
                            <Trans
                              i18nKey="campaigns_page.campaign_settings.click_hint.camp"
                              components={{ br: <br />, b: <strong /> }}
                            />
                          ),
                        }}
                      />
                    )}
                    <div className={css.tags}>
                      <div className={css.label}>
                        {t(
                          'campaigns_page.campaign_settings.additional.aggregation',
                        )}
                      </div>
                      <RSelect
                        placeholder={t(
                          'campaigns_page.campaign_settings.additional.ddmm',
                        )}
                        classNamePrefix="tags-select"
                        value={aggregationTag}
                        onChange={onChangeAggregation}
                        isSearchable
                        name="tags"
                        options={optTag}
                        onInputChange={onChangeText}
                        isClearable
                        components={componentsSelect}
                      />
                    </div>
                    <div>
                      <div className={css.label}>
                        {t('clients_page.client_edit.moderation_creatives')}
                      </div>
                      <ToggleSwitch
                        disabled={isDisableEridToggle(campaign, 'CAMPAIGN')}
                        onChange={() =>
                          formik.setFieldValue(
                            'check_ERID',
                            !formik.values.check_ERID,
                          )
                        }
                        checked={formik.values.check_ERID}
                        activateText={t('creative.widget_text.check_ERID')}
                        deactivateText={t('creative.widget_text.check_ERID')}
                      />
                    </div>
                  </div>
                </CollapseCard>
              </Card>
              <LinkingExternal formik={formik} />
              <AdvancedSettings/>
              <Reports
                formik={formik}
                convertReports={convertReports}
                audiences={audiences}
                setCurrentAudit={setCurrentAudit}
                campaign={campaign}
              />
              <ExtendedReport formik={formik} />
              <div className={css.actions}>
                <Button
                  title={t('save_btn')}
                  height={40}
                  buttonAttributes={{
                    type: 'submit',
                    disabled: isLoadingEdit || !formik.isValid,
                  }}
                />
              </div>
            </Form>
          </CardsList>
        </FormikProvider>
        <Widget />
      </div>
    </>
  );
};

export default Settings;
